import { RemoveAnyRecord } from "./types/utils/RemoveAnyRecord";
import { FastEventScope, IFastEventScope, type FastEventScopeOptions } from "./scope";
import {
    GetClosestEvents,
    KeyOf,
    MutableMessage,
    Expand,
    Fallback,
    ReplaceWildcard,
    UnTransformedEvents,
    FastEventMessage,
    GetClosestEventPayload,
} from "./types";
import {
    TypedFastEventMessage,
    FastEventEmitMessage,
    FastEventMeta,
} from "./types/FastEventMessages";
import {
    FastEventListeners,
    FastListeners,
    TypedFastEventAnyListener,
} from "./types/FastEventListeners";
import { FastEventSubscriber } from "./types/FastEventSubscribers";
import {
    TypedFastEventListener,
    FastEventListenerNode,
    FastEventListenerMeta,
    FastEventCommonListener,
} from "./types/FastEventListeners";
import {
    FastEventOptions,
    FastEventListenOptions,
    FastEventListenerArgs,
    FastEvents,
    FastEventListenerFlags,
} from "./types/FastEvents";
import { IsTransformedEvent } from "./types/transformed/IsTransformedEvent";
import { ExtendWildcardEvents } from "./types/wildcards/ExtendWildcardEvents";
import { PayloadValues } from "./types/transformed/PayloadValues";
import { PickPayload } from "./types/transformed/PickPayload";
import { ValueOf } from "./types/utils/ValueOf";
import { DeepPartial } from "./types/utils/DeepPartial";
import { GetPayload } from "./types/transformed/GetPayload";
import { IsAllTransformed } from "./types/transformed/IsAllTransformed";
import { parseEmitArgs } from "./utils/parseEmitArgs";
import { isPathMatched } from "./utils/isPathMatched";
import { removeItem } from "./utils/removeItem";
import { renameFn } from "./utils/renameFn";
import { isFunction } from "./utils/isFunction";
import { collectBroadcastTargets } from "./utils/collectBroadcastTargets";
import { ScopeEvents } from "./types";
import { AbortError, CancelError } from "./consts";
import { parseScopeArgs } from "./utils/parseScopeArgs";
import { FastListenerExecutor, parallel } from "./executors";
import { expandEmitResults } from "./utils/expandEmitResults";
import { isSubsctiber } from "./utils/isSubsctiber";
import { tryReturnError } from "./utils/tryReturnError";
import {
    createAsyncEventIterator,
    FastEventIterator,
    FastEventIteratorOptions,
    WithListenerDecorator,
} from "./iterator";
import { InMatchedEvent } from "./types/wildcards/InMatchedEvent";
import { GetClosestMessage } from "./types/closest/GetClosestMessage";
import { wrapPipeListener } from "./utils/wrapPipeListener";
import { getWeakRef } from "./utils/getWeakRef";
import { getPromiseResults } from "./utils/getPromiseResults";
import { isPromise } from "./utils/isPromise";
import { resolveValue } from "./utils/resolveValue";
import { AfterExecuteListenerHook, FastEventHooks } from "./types/FastEventHooks";
import { ItemOf } from "../../viewer/src/types";
import { isAsyncIterable } from "./utils/isAsyncIterable";
import { getListeners } from "./utils/getListeners";

/**
 * FastEvent 事件发射器类
 *
 * @template Events - 事件类型定义，继承自FastEvents接口
 * @template Meta - 事件元数据类型，默认为任意键值对对象
 * @template Types - 事件类型的键名类型，默认为Events的键名类型
 */
export class FastEvent<
    Events extends Record<string, any> = Record<string, any>,
    Meta extends Record<string, any> = Record<string, any>,
    Context = never,
    // 泛型快捷方式
    AllEvents extends Record<string, any> = Expand<Events & FastEvents>,
    Types extends keyof AllEvents = KeyOf<AllEvents>,
    AllMeta extends Record<string, any> = FastEventMeta & Meta,
    EventNames = KeyOf<ExtendWildcardEvents<AllEvents>>,
> {
    __FastEvent__: boolean = true;

    /** 事件监听器树结构，存储所有注册的事件监听器 */
    public listeners: FastListeners = { __listeners: [] } as unknown as FastListeners;

    /** 事件发射器的配置选项 */
    private _options: FastEventOptions<Meta, Context>;

    /** 事件名称的分隔符，默认为'/' */
    private _delimiter: string = "/";

    /** 缓存的 transform 函数（构造时规范化：非函数则 undefined）。emit 热路径直接判断，省属性链 + isFunction */
    private _transform: any;

    /** 缓存的 expandEmitResults 开关（构造时读取，默认 true） */
    private _expandResults: boolean = true;

    /** 事件监听器执行时的上下文对象 */
    private _context: Context;

    private _hooks?: FastEventHooks;
    /** 保留的事件消息映射，Key是事件名称，Value是保留的事件消息 */
    retainedMessages: Map<string, any> = new Map<string, any>();

    /** 当前注册的监听器总数 */
    listenerCount: number = 0;

    // 子类通过：declare types : { } & typeof FastEvent.types  扩展类型
    types = null as unknown as {
        events: ExtendWildcardEvents<AllEvents>;
        eventNames: KeyOf<ExtendWildcardEvents<AllEvents>>;
        meta: AllMeta;
        context: Expand<Fallback<Context, FastEvent<AllEvents, Meta, Context>>>;
        messages: MutableMessage<AllEvents, Meta>;
        message: MutableMessage<AllEvents, Meta>;
        listeners: FastEventListeners<
            AllEvents,
            Expand<FastEventMeta & Meta & Record<string, any>>
        >;
        anyListener: TypedFastEventAnyListener<
            AllEvents,
            Expand<FastEventMeta & Meta & Record<string, any>>
        >;
        listener: FastEventCommonListener<
            FastEventMessage,
            Expand<FastEventMeta & Meta & Record<string, any>>
        >;
    };
    /**
     * 创建FastEvent实例
     * @param options - 事件发射器的配置选项
     *
     * 默认配置：
     * - debug: false - 是否启用调试模式
     * - id: 随机字符串 - 实例唯一标识符
     * - delimiter: '/' - 事件名称分隔符
     * - context: null - 监听器执行上下文
     * - ignoreErrors: true - 是否忽略监听器执行错误
     */
    constructor(options?: Partial<FastEventOptions<Meta, Context>>) {
        this._options = Object.assign(
            {
                debug: false,
                id: Math.random().toString(36).substring(2),
                delimiter: "/",
                context: null,
                ignoreErrors: true,
                meta: undefined,
                expandEmitResults: true,
                executor: parallel(),
            },
            this._initOptions(options),
        ) as unknown as FastEventOptions<Meta, Context>;
        this._delimiter = this._options.delimiter!;
        this._context = this._options.context as Context;
        // 缓存热路径配置，避免 emit 每次读取属性链 + isFunction
        const transformOpt = this._options.transform;
        this._transform = typeof transformOpt === "function" ? transformOpt : undefined;
        this._expandResults = this._options.expandEmitResults !== false;
    }

    /** 获取事件发射器的配置选项 */
    get options() {
        return this._options;
    }
    get context(): Fallback<Context, typeof this> {
        return (this.options.context || this) as Fallback<Context, typeof this>;
    }
    get meta() {
        return this.options.meta;
    }
    /** 获取事件发射器的唯一标识符 */
    get id() {
        return this._options.id!;
    }
    /** 获取事件发射器的唯一标识符 */
    get title() {
        return this._options.title || this.id || "FastEvent";
    }
    get hooks(): FastEventHooks {
        if (!this._hooks) {
            this._hooks = {
                AddBeforeListener: [],
                AddAfterListener: [],
                RemoveListener: [],
                ClearListeners: [],
                ListenerError: [],
                BeforeExecuteListener: [],
                AfterExecuteListener: [],
            };
        }
        return this._hooks;
    }
    private _execAfterExecuteListener(fn: Function, args: Parameters<AfterExecuteListenerHook>) {
        Promise.allSettled(args[1]).then((r: any[]) => {
            args[1] = getPromiseResults(r);
            fn!.apply(this, args as any);
        });
    }
    /**
     * 执行Hook
     *
     * AfterExecuteListener为什么需要特别处理？
     *
     * 因为AfterExecuteListener是在监听器执行完成后调用
     * 并且将监听器的结果传入，但是监听器有可能返回Promise
     * 因为需要等等Promise resolve，再调用AfterExecuteListener
     *
     *
     * @param hookName
     * @param args
     * @param onlyAsyncHook 只运行异步HOOK
     * @returns
     */
    private _executeHooks<T extends keyof FastEventHooks>(
        hookName: T,
        args: Parameters<ItemOf<FastEventHooks[T]>>,
        onlyAsyncHook: boolean = false,
    ) {
        if (this._hooks) {
            setTimeout(() => {
                const hooks = this.hooks[hookName];
                if (Array.isArray(hooks) && hooks.length > 0) {
                    Promise.allSettled(
                        hooks.map((hook) => {
                            if (hookName === "AfterExecuteListener") {
                                this._execAfterExecuteListener(hook, args as any);
                            } else {
                                return (hook as any).apply(this, args);
                            }
                        }),
                    );
                }
            });
        }

        if (!onlyAsyncHook) {
            const hookMethod = this.options[`on${hookName}`] as Function;
            if (isFunction(hookMethod)) {
                if (hookName === "AfterExecuteListener") {
                    this._execAfterExecuteListener(hookMethod, args as any);
                } else {
                    return hookMethod.apply(this, args);
                }
            }
        }
    }
    /**
     * 初始化选项
     *
     * 本方法主要供子类重载
     *
     * @param initial - 可选的初始字典对象
     * @returns 返回传入的初始字典对象
     */
    _initOptions(initial?: Partial<FastEventOptions<Meta, Context>>) {
        return initial;
    }
    /**
     * 添加事件监听器到事件树中
     * @param parts - 事件路径数组
     * @param listener - 事件监听器函数
     * @param options - 监听器配置选项
     * @param options.count - 监听器触发次数限制
     * @param options.prepend - 是否将监听器添加到监听器列表开头
     * @returns [节点, 监听器索引] - 返回监听器所在节点和在节点监听器列表中的索引
     * @private
     */
    private _addListener(
        parts: string[],
        listener: TypedFastEventListener<any, any>,
        options: Required<FastEventListenOptions>,
    ): [FastEventListenerNode | undefined, number] {
        const { count, prepend } = options;
        let index: number = 0;
        const node = this._forEachNodes(parts, (node) => {
            const newListener = [
                listener,
                count,
                0,
                options.tag,
                options.flags,
            ] as unknown as FastEventListenerMeta;
            if (prepend) {
                node.__listeners.splice(0, 0, newListener);
                index = 0;
            } else {
                node.__listeners.push(newListener);
                index = node.__listeners.length - 1;
            }
            this.listenerCount++;
        });
        return [node, index];
    }
    /**
     *
     * 根据parts路径遍历监听器树，并在最后的节点上执行回调函数
     *
     * @param parts
     * @param callback
     * @returns
     */
    private _forEachNodes(
        parts: string[],
        callback: (node: FastEventListenerNode, parent: FastEventListenerNode) => void,
    ): FastEventListenerNode | undefined {
        if (parts.length === 0) return;
        let current = this.listeners;
        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (!(part in current)) {
                current[part] = {
                    __listeners: [],
                } as unknown as FastListeners;
            }
            if (i === parts.length - 1) {
                const node = current[part];
                callback(node, current);
                return node;
            } else {
                current = current[part];
            }
        }
        return undefined;
    }

    /**
     * 从监听器节点中移除指定的事件监听器
     * @private
     * @param node - 监听器节点
     * @param listener - 需要移除的事件监听器
     * @description 遍历节点的监听器列表,移除所有匹配的监听器。支持移除普通函数和数组形式的监听器
     */
    private _removeListener(
        node: FastEventListenerNode,
        path: string[],
        listener: TypedFastEventListener<any, any, any>,
    ): void {
        if (!listener) return;
        removeItem(node.__listeners, (item: any) => {
            item = Array.isArray(item) ? item[0] : item;
            const isRemove = item === listener;
            if (isRemove) {
                this.listenerCount--;
                this._executeHooks("RemoveListener", [path.join(this._delimiter), listener, node]);
            }
            return isRemove;
        });
    }
    /**
     * 创建异步迭代监听器
     * @param type
     * @param options
     * @returns
     */
    private _createAyncIteratorListener(type: string, options: FastEventListenOptions): any {
        const iteratorOpts = Object.assign(
            {
                overflow: "expand",
                size: 10,
                maxExpandSize: 100,
            },
            options.iterable,
        ) as FastEventIteratorOptions;
        const iterator = createAsyncEventIterator<any>(this as any, type, iteratorOpts);
        iterator.create(options);
        const r = this._executeHooks("AddBeforeListener", [type, iterator.listener, options]);
        if (r === false) {
            throw new CancelError();
        } else if (isAsyncIterable(r)) {
            return r;
        }
        // 返回可调用的迭代器，用作装饰器工厂
        return iterator;
    }
    /**
     * 注册事件监听器
     * @param type - 事件类型，支持以下格式：
     *   - 普通字符串：'user/login'
     *   - 通配符：'user/*'（匹配单层）或'user/**'（匹配多层）
     *   - 全局监听：'**'（监听所有事件）
     * @param listener - 事件监听器函数
     * @param options - 监听器配置选项：
     *   - count: 触发次数限制，0表示无限制
     *   - prepend: 是否将监听器添加到监听器队列开头
     * @returns 返回订阅者对象，包含off方法用于取消监听
     *
     * @example
     * ```ts
     * // 监听特定事件
     * emitter.on('user/login', (data) => console.log(data));
     *
     * // 使用通配符
     * emitter.on('user/*', (data) => console.log(data));
     *
     * // 限制触发次数
     * emitter.on('event', handler, { count: 3 });
     * ```
     */
    // 返回迭代器
    public on<T extends string = KeyOf<Events> | "**">(
        type: T,
        options?: FastEventListenOptions<AllEvents, Meta>,
    ): T extends IsTransformedEvent<AllEvents, T>
        ? WithListenerDecorator<
              FastEventIterator<
                  T extends "**"
                      ? IsAllTransformed<Events> extends true
                          ? PayloadValues<AllEvents>
                          : any
                      : PickPayload<ValueOf<GetClosestEvents<AllEvents, T>>>
              >
          >
        : WithListenerDecorator<
              FastEventIterator<
                  T extends "**" ? MutableMessage<AllEvents> : GetClosestMessage<Events, T, Meta>
              >
          >;

    // 指定监听器
    public on<T extends string = KeyOf<Events> | "**">(
        type: T,
        listener: FastEventCommonListener<
            T extends IsTransformedEvent<AllEvents, T>
                ? PickPayload<ValueOf<GetClosestEvents<Events, T>>>
                : T extends "**"
                  ? MutableMessage<Events>
                  : GetClosestMessage<Events, T, Meta>,
            Meta,
            Fallback<Context, typeof this>
        >,
        options?: FastEventListenOptions,
    ): FastEventSubscriber;

    public on(): any {
        const type = arguments[0] as string;
        if (type.length === 0) throw new Error("event cannot be empty");

        // 解析参数和选项
        let listener: any = null;
        let options: any;

        // 当不提供监听器时返回异步迭代器
        const noListener = !isFunction(arguments[1]);
        if (noListener) {
            options = arguments[1] || {};
        } else {
            // on(type, listener, options?)
            listener = arguments[1];
            options = arguments[2] || {};
        }

        const finalOptions = Object.assign(
            {
                count: 0,
                flags: 0,
                prepend: false,
            },
            options,
        ) as Required<FastEventListenOptions>;

        // 当未指定监听器时返回一个异步迭代器
        if (noListener || listener === null) {
            return this._createAyncIteratorListener(type, finalOptions);
            const iteratorOpts = Object.assign(
                {
                    overflow: "expand",
                    size: 10,
                    maxExpandSize: 100,
                },
                finalOptions.iterable,
            ) as FastEventIteratorOptions;
            const iterator = createAsyncEventIterator<any>(this as any, type, iteratorOpts);
            iterator.create(finalOptions);
            const r = this._executeHooks("AddBeforeListener", [
                type,
                iterator.listener,
                finalOptions,
            ]);
            if (r === false) {
                throw new CancelError();
            } else if (isAsyncIterable(r)) {
                return r;
            }
            return iterator;
        }
        // 执行回调
        const r = this._executeHooks("AddBeforeListener", [type, listener, finalOptions]);
        if (r === false) {
            throw new CancelError();
        } else if (isSubsctiber(r)) {
            return r;
        }

        const parts = type.split(this._delimiter);

        // 处理 pipes（适用于所有订阅者，包括 iterable）
        if (finalOptions.pipes && finalOptions.pipes.length > 0) {
            listener = wrapPipeListener(listener, finalOptions.pipes);
        }

        // 处理 filter 和 off（仅用于普通订阅者）
        if (isFunction(finalOptions.filter) || isFunction(finalOptions.off)) {
            const oldListener = listener;
            const off = () => node && this._removeListener(node, parts, listener);
            listener = renameFn<TypedFastEventListener>(function (message, args) {
                // 如果满足条件就退订
                if (
                    isFunction(finalOptions.off) &&
                    finalOptions.off.call(this, message as any, args)
                ) {
                    off();
                    return;
                }
                // 如果满足条件就触发监听器
                if (isFunction(finalOptions.filter)) {
                    if (finalOptions.filter.call(this, message as any, args!))
                        return oldListener.call(this, message, args);
                } else {
                    return oldListener.call(this, message, args);
                }
            }, listener.name);
        }

        // 添加到监听器树
        const [node, index] = this._addListener(parts, listener, finalOptions);
        const off = () => node && this._removeListener(node, parts, listener);

        this._executeHooks("AddAfterListener", [type, node!]);

        // 触发监听器保留消息
        this._emitRetainMessage(type, node, index);

        return {
            off,
            listener,
            [Symbol.dispose]() {
                off();
            },
        };
    }

    /**
     * 注册一次性事件监听器
     * @param type - 事件类型，支持与on方法相同的格式：
     *   - 普通字符串：'user/login'
     *   - 通配符：'user/*'（匹配单层）或'user/**'（匹配多层）
     * @param listener - 事件监听器函数
     * @returns 返回订阅者对象，包含off方法用于取消监听
     *
     * @description
     * 监听器只会在事件首次触发时被调用一次，之后会自动解除注册。
     * 这是on方法的特例，相当于设置options.count = 1。
     * 如果事件有保留消息，新注册的监听器会立即收到最近一次的保留消息并解除注册。
     *
     * @example
     * ```ts
     * // 只监听一次登录事件
     * emitter.once('user/login', (data) => {
     *   console.log('用户登录:', data);
     * });
     * ```
     */
    public once<T extends string = KeyOf<Events> | "**">(
        type: T,
        listener: FastEventCommonListener<
            T extends IsTransformedEvent<AllEvents, T>
                ? PickPayload<ValueOf<GetClosestEvents<Events, T>>>
                : T extends "**"
                  ? MutableMessage<Events>
                  : GetClosestMessage<Events, T, Meta>,
            Meta,
            Fallback<Context, typeof this>
        >,
        options?: FastEventListenOptions,
    ): FastEventSubscriber;
    public once(): FastEventSubscriber {
        if (isFunction(arguments[1])) {
            return this.on(
                arguments[0],
                arguments[1],
                Object.assign({}, arguments[2], { count: 1 }),
            );
        } else {
            return this.on(
                arguments[0],
                Object.assign({}, arguments[2], { count: 1 }),
            ) as unknown as FastEventSubscriber;
        }
    }

    /**
     * 注册一个监听器，用于监听所有事件
     * @param listener 事件监听器函数，可以接收任意类型的事件数据
     * @returns {FastEventSubscriber} 返回一个订阅者对象，包含 off 方法用于取消监听
     * @example
     * ```ts
     * const subscriber = emitter.onAny((eventName, data) => {
     *   console.log(eventName, data);
     * });
     *
     * // 取消监听
     * subscriber.off();
     * ```
     */
    // 返回迭代器（不指定监听器时）
    public onAny(
        options?: FastEventListenOptions<AllEvents, AllMeta>,
    ): FastEventIterator<MutableMessage<AllEvents, AllMeta>>;
    // 指定监听器
    public onAny(
        listener: FastEventCommonListener<
            MutableMessage<AllEvents, AllMeta>,
            Meta,
            Fallback<Context, typeof this>
        >,
        options?: FastEventListenOptions<AllEvents, AllMeta>,
    ): FastEventSubscriber;
    public onAny() {
        return this.on("**", ...arguments) as any;
    }
    /**
     *
     * 当调用on/once/onAny时如果没有指定监听器，则调用此方法
     *
     * 此方法供子类继承
     *
     */
    off(listener: TypedFastEventListener<any, any, any>): void;
    off(type: string, listener: TypedFastEventListener<any, any, any>): void;
    off(type: Types, listener: TypedFastEventListener<any, any, any>): void;
    off(type: string): void;
    off(type: Types): void;
    off() {
        const args = arguments;
        const type = isFunction(args[0]) ? undefined : args[0];
        const listener = isFunction(args[0]) ? args[0] : args[1];
        const parts = type ? type.split(this._delimiter) : [];
        const hasWildcard = type ? type.includes("*") : false;
        if (type && !hasWildcard) {
            this._traverseToPath(this.listeners, parts, (node) => {
                if (listener) {
                    // 只删除指定的监听器
                    this._removeListener(node, parts, listener);
                } else if (type) {
                    node.__listeners = [];
                }
            });
        } else {
            // 仅删除指定的监听器
            const entryParts: string[] = hasWildcard ? [] : parts;
            this._traverseListeners(this.listeners, entryParts, (path, node) => {
                if (listener !== undefined || (hasWildcard && isPathMatched(path, parts))) {
                    if (listener) {
                        this._removeListener(node, parts, listener);
                    } else {
                        node.__listeners = [];
                    }
                }
            });
        }
    }

    /**
     * 移除所有事件监听器
     * @param entry - 可选的事件前缀,如果提供则只移除指定前缀下的的监听器
     * @description 
     * - 如果提供了prefix参数,则只清除该前缀下的所有监听器
     * - 如果没有提供prefix,则清除所有监听器
     * - 同时会清空保留的事件(_retainedEvents)
     * - 重置监听器对象为空
     
    * @example
     * 
     * ```ts
     * emitter.offAll();    // 清除所有监听器
     * emitter.offAll('a/b'); // 清除a/b下的所有监听器
     * 
     */
    offAll(entry?: string) {
        if (entry) {
            const parts = entry.split(this._delimiter);
            let count = 0;
            this._traverseListeners(this.listeners, parts, (path, node) => {
                count += node.__listeners.length;
                try {
                    node.__listeners.forEach((listener) => {
                        this._executeHooks("RemoveListener", [
                            path.join(this.options.delimiter),
                            listener[0],
                            node,
                        ]);
                    });
                } finally {
                    node.__listeners = [];
                }
            });
            this.listenerCount -= count;
            this._removeRetainedEvents(entry);
        } else {
            try {
                let count = 0;
                this._traverseListeners(this.listeners, [], (path, node) => {
                    count += node.__listeners.length;
                });
                this.listenerCount -= count;
                this.retainedMessages.clear();
                this.listeners = { __listeners: [] } as unknown as FastListeners;
            } finally {
                this._executeHooks("ClearListeners", []);
            }
        }
    }
    /**
     * 移除保留的事件
     * @param prefix - 事件前缀。如果不提供，将清除所有保留的事件。
     *                如果提供前缀，将删除所有以该前缀开头的事件。
     *                如果前缀不以分隔符结尾，会自动添加分隔符。
     * @private
     */
    private _removeRetainedEvents(prefix?: string) {
        if (!prefix) this.retainedMessages.clear();
        if (prefix?.endsWith(this._delimiter)) {
            prefix += this._delimiter;
        }
        this.retainedMessages.delete(prefix!);
        for (let key of this.retainedMessages.keys()) {
            if (key.startsWith(prefix!)) {
                this.retainedMessages.delete(key);
            }
        }
    }
    clear(prefix?: string) {
        this.offAll(prefix);
        this._removeRetainedEvents(prefix);
    }

    /**
     * 发送最后一次事件的消息给对应的监听器
     *
     * @param type - 事件类型,支持通配符(*)匹配
     * @private
     *
     * 处理流程:
     * 1. 如果事件类型包含通配符,则遍历所有保留的消息,匹配符合模式的事件
     * 2. 如果是普通事件类型,则直接获取对应的保留消息
     * 3. 遍历匹配到的消息,查找对应路径的监听器节点
     * 4. 执行所有匹配到的监听器
     */
    private _emitRetainMessage(
        type: string,
        listenerNode: FastEventListenerNode | undefined,
        index: number,
    ) {
        let messages = [] as TypedFastEventMessage[];
        if (type.includes("*")) {
            // 找出所有保留的消息
            const patterns = type.split(this._delimiter);
            this.retainedMessages.forEach((message, type) => {
                const parts = type.split(this._delimiter);
                if (isPathMatched(parts, patterns)) {
                    messages.push(message);
                }
            });
        } else if (this.retainedMessages.has(type)) {
            messages.push(this.retainedMessages.get(type));
        }
        if (listenerNode) {
            messages.forEach((message) => {
                this._executeListeners([listenerNode], message, {}, (listenerMeta) => {
                    return listenerMeta[0] === listenerNode.__listeners[index][0];
                });
            });
        }
    }

    /**
     * 遍历监听器节点树
     * @param node 当前遍历的监听器节点
     * @param parts 事件名称按'.'分割的部分数组
     * @param callback 遍历到目标节点时的回调函数
     * @param index 当前遍历的parts数组索引,默认为0
     * @param lastFollowing  当命中**时该值为true, 注意**只能作在路径的最后面，如a.**有效，而a.**.b无效
     * @private
     *
     * 支持三种匹配模式:
     * - 精确匹配: 'a.b.c'
     * - 单层通配: 'a.*.c'
     * - 多层通配: 'a.**'
     */
    private _traverseToPath(
        node: FastEventListenerNode,
        parts: string[],
        callback: (node: FastEventListenerNode) => void,
        index: number = 0,
        lastFollowing?: boolean,
    ): void {
        if (index >= parts.length) {
            callback(node);
            return;
        }
        const part = parts[index];

        if (lastFollowing === true) {
            this._traverseToPath(node, parts, callback, index + 1, true);
            return;
        }

        if ("*" in node) {
            this._traverseToPath(node["*"], parts, callback, index + 1);
        }
        //
        if ("**" in node) {
            this._traverseToPath(node["**"], parts, callback, index + 1, true);
        }

        if (part in node) {
            this._traverseToPath(node[part], parts, callback, index + 1);
        }
    }

    private _traverseListeners(
        node: FastEventListenerNode,
        entry: string[],
        callback: (path: string[], node: FastEventListenerNode) => void,
    ): void {
        let entryNode: FastEventListenerNode = node;
        // 如果指定了entry路径，则按照路径遍历
        if (entry && entry.length > 0) {
            this._traverseToPath(node, entry, (node) => {
                entryNode = node;
            });
        }
        const traverseNodes = (
            node: FastEventListenerNode,
            callback: (path: string[], node: FastEventListenerNode) => void,
            parentPath: string[],
        ) => {
            callback(parentPath, node);
            for (let [key, childNode] of Object.entries(node)) {
                if (key.startsWith("__")) continue;
                if (childNode) {
                    traverseNodes(childNode as FastEventListenerNode, callback, [
                        ...parentPath,
                        key,
                    ]);
                }
            }
        };
        // 如果没有指定entry或entry为空数组，则递归遍历所有节点
        traverseNodes(entryNode, callback, []);
    }

    private _onListenerError(
        listener: TypedFastEventListener,
        message: TypedFastEventMessage,
        args: FastEventListenerArgs<any> | undefined,
        e: any,
    ) {
        if (e instanceof Error) {
            // @ts-ignore
            e._emitter = `${listener.name || "anonymous"}:${message.type}`;
        }
        this._executeHooks("ListenerError", [e, listener, message, args]);
        if (this._options.ignoreErrors) {
            return e;
        } else {
            throw e;
        }
    }
    /**
     * 执行单个监听器函数
     * @param listener - 要执行的监听器函数或包装过的监听器对象
     * @param message - 事件消息对象，包含type、payload和meta
     * @param args - 监听器参数
     * @param catchErrors - 是否捕获并处理执行过程中的错误
     * @returns 监听器的执行结果或错误对象（如果配置了ignoreErrors）
     * @private
     *
     * @description
     * 执行单个监听器函数，处理以下情况：
     * - 如果监听器是包装过的（有__wrappedListener属性），调用包装的函数
     * - 否则直接调用监听器函数
     * - 使用配置的上下文（_context）作为this
     * - 捕获并处理执行过程中的错误：
     *   - 如果有onListenerError回调，调用它
     *   - 如果配置了ignoreErrors，返回错误对象
     *   - 否则抛出错误
     */
    private _executeListener(
        listener: FastEventListenerMeta,
        message: TypedFastEventMessage,
        args: FastEventListenerArgs<any> | undefined,
        catchErrors: boolean = false,
    ): Promise<any> | any {
        const listenerFn = listener[0];
        try {
            if (this.options.debug) {
                listener.length = 5;
            }
            // 如果传入已经aborted的abortSignal，则直接返回
            if (args && args.abortSignal && args.abortSignal.aborted) {
                return this._onListenerError(
                    listenerFn,
                    message,
                    args,
                    new AbortError(listener[0].name),
                );
            }
            const isTransformed = ((args?.flags || 0) & FastEventListenerFlags.Transformed) > 0;

            let result = listenerFn.call(
                this.context,
                isTransformed ? message.payload : message,
                args!,
            );
            // 自动处理reject Promise
            if (catchErrors && result && result instanceof Promise) {
                result = tryReturnError(result, (e) =>
                    this._onListenerError(listenerFn, message, args, e),
                );
            }
            // 在调试模式下，在节点保存返回结果
            if (this.options.debug) {
                Promise.resolve(result);
                if (isPromise(result)) {
                    resolveValue(result).then((r) => {
                        listener[5] = getWeakRef(r);
                    });
                } else {
                    listener[5] = getWeakRef(result);
                }
            }
            return result;
        } catch (e: any) {
            const errObj = this._onListenerError(listenerFn, message, args, e);
            if (errObj instanceof Error) listener[5] = getWeakRef(errObj);
            return errObj;
        }
    }
    private _getListenerExecutor(args: FastEventListenerArgs): FastListenerExecutor | undefined {
        if (!args) return;
        const executor = args.executor || this._options.executor;
        if (isFunction(executor)) return executor;
    }

    /**
     * 执行监听器节点列表中的所有监听器函数
     * @param nodes 监听器节点列表
     * @param message 事件消息对象
     * @param args 监听器参数
     * @param args 监听器参数
     * @returns 所有监听器函数的执行结果数组
     * @private
     *
     * 执行流程:
     * 1. 将所有节点中的监听器函数展平为一维数组
     * 2. 通过执行器执行所有监听器函数
     * 3. 更新监听器的执行次数,并移除达到执行次数限制的监听器
     */
    private _executeListeners(
        nodes: FastEventListenerNode[],
        message: TypedFastEventMessage,
        args: FastEventListenerArgs<Meta>,
        filter?: (listener: FastEventListenerMeta, node: FastEventListenerNode) => boolean,
    ): any[] {
        if (!nodes || nodes.length === 0) return [];
        // A3 三遍合一：原"收集 → _decListenerExecCount(计数+splice) → 执行"合并为
        // "收集含计数 → 倒序 splice → 执行"，省一遍全量遍历与临时元组分配。
        // 计数仍严格先于执行，保留"避免监听器内再次触发导致重复执行"的语义。
        const metas: FastEventListenerMeta[] = [];
        const toRemove: [FastEventListenerMeta[], number][] = [];
        for (const node of nodes) {
            // localIdx 必须等于 listener 在 __listeners 中的真实数组下标，
            // 因此 i 对每个 listener 都递增（含被 filter 跳过的），不能只在命中时递增
            const arr = node.__listeners as unknown as FastEventListenerMeta[];
            let i = 0;
            for (const meta of arr) {
                if (!filter || filter(meta, node)) {
                    meta[2]++; // 实际执行次数
                    metas.push(meta);
                    // =0不限执行次数，>0时代表执行次数限制
                    if (meta[1] > 0 && meta[1] <= meta[2]) {
                        toRemove.push([arr, i]);
                        this.listenerCount--; // 同步递减，与 off/offAll 路径一致
                    }
                }
                i++;
            }
        }
        // 倒序 splice：toRemove 收集顺序为升序，倒序遍历使同节点内 idx 降序，避免删元素错位
        for (let i = toRemove.length - 1; i >= 0; i--) {
            toRemove[i][0].splice(toRemove[i][1], 1);
        }

        const executeor = this._getListenerExecutor(args);
        if (executeor) {
            const r = executeor(metas, message, args, this._executeListener.bind(this)) as any[];
            return Array.isArray(r) ? r : [r];
        }
        const results: any[] = [];
        for (let i = 0; i < metas.length; i++) {
            results.push(this._executeListener(metas[i], message, args, true));
        }
        return results;
    }

    /**
     * 获取指定类型的所有事件监听器
     * @param type - 事件类型，必须是 AllEvents 的键
     * @returns 包含指定类型的所有监听器元数据的数组
     */
    getListeners(type: keyof AllEvents): FastEventListenerMeta[];
    getListeners(type: string): FastEventListenerMeta[] {
        return getListeners(this as any, type);
    }
    /**
     * 清除所有事件或指定事件的保留消息
     * @param type
     */
    clearRetainMessages(type?: EventNames | string) {
        if (type) {
            this.retainedMessages.delete(type as string);
        } else {
            this.retainedMessages.clear();
        }
    }
    /**
     * 触发事件并执行对应的监听器
     *
     * @param type - 事件类型字符串或包含事件信息的对象
     * @param payload - 事件携带的数据负载
     * @param retain - 是否保留该事件(用于新订阅者)
     * @param meta - 事件元数据
     * @returns 所有监听器的执行结果数组
     *
     * @example
     * // 方式1: 参数形式
     * emit('user.login', { id: 1 }, true)
     *
     * // 方式2: 对象形式
     * emit({ type: 'user.login', payload: { id: 1 } ,meta:{...}}}, true)
     */
    /**
     * 同步触发事件
     * @param type - 事件类型，可以是字符串或预定义的事件类型
     * @param payload - 事件数据负载
     * @param retain - 是否保留该事件，用于后续新的订阅者
     * @param meta - 事件元数据
     * @returns 所有监听器的执行结果数组
     *
     * @description
     * 同步触发指定类型的事件，支持两种调用方式：
     * 1. 参数形式：emit(type, payload, retain, meta)
     * 2. 对象形式：emit({ type, payload, meta }, retain)
     *
     * 特性：
     * - 支持通配符匹配，一个事件可能触发多个监听器
     * - 如果设置了retain为true，会保存最后一次的事件数据
     * - 按照注册顺序同步调用所有匹配的监听器
     * - 如果配置了ignoreErrors，监听器抛出的错误会被捕获并返回
     *
     * @example
     * ```ts
     * // 简单事件触发
     * emitter.emit('user/login', { userId: 123 });
     *
     * // 带保留的事件触发
     * emitter.emit('status/change', { online: true }, true);
     *
     * // 带元数据的事件触发
     * emitter.emit('data/update', newData, false, { timestamp: Date.now() });
     *
     * // 使用对象形式触发
     * emitter.emit({
     *   type: 'user/login',
     *   payload: { userId: 123 },
     *   meta: { time: Date.now() }
     * }, true);
     *
     * // 清除保留事件
     * emitter.emit("user/login")
     *
     * ```
     *
     * 为什么事件要使用UnTransformedEvents？
     *
     * 因为如果事件使用NotPayload，则会进行转换
     *
     */
    public emit<R = any, T extends Types = Types>(
        type: T,
        payload?: UnTransformedEvents<AllEvents>[T],
        retain?: boolean,
    ): R[];
    public emit<R = any, T extends string = string>(
        type: ReplaceWildcard<T> | Types,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<AllEvents>, T>
            : any,
        retain?: boolean,
    ): R[];
    public emit<R = any, T extends string = string>(
        type: ReplaceWildcard<T> | Types,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<AllEvents>, T>
            : any,
        options?: FastEventListenerArgs<Partial<Meta>>,
    ): R[];
    public emit<R = any, T extends KeyOf<AllEvents> = KeyOf<AllEvents>>(
        message: FastEventEmitMessage<T, UnTransformedEvents<AllEvents>[T], Partial<Meta>>,
        retain?: boolean,
    ): R[];
    public emit<R = any>(message: MutableMessage<AllEvents, Meta>, retain?: boolean): R[];
    public emit<R = any>(
        message: {
            type: keyof AllEvents;
        },
        retain?: boolean,
    ): R[];
    public emit<R = any>(message: FastEventMessage, retain?: boolean): R[];
    public emit<R = any, T extends string = string>(
        type: T,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<AllEvents>, T>
            : any,
        retain?: boolean,
    ): R[];
    public emit(): any {
        const [message, args] = parseEmitArgs<AllEvents, Meta>(arguments, this.options.meta);

        if (isFunction(args.parseArgs)) {
            args.parseArgs(message, args);
        }
        const type = message.type;
        const delimiter = this._delimiter;
        const root = this.listeners;
        // A1 单段快路径：type 不含分隔符时跳过 split + _traverseToPath 递归
        const singleSegment = type.indexOf(delimiter) === -1;
        const parts = singleSegment ? null : type.split(delimiter);
        if (args.retain) {
            this.retainedMessages.set(type, message);
        }

        // A2 transform 缓存（构造时规范化：非函数则 undefined）
        const transformFn = this._transform;

        // 1. 正常匹配节点(emit 路径直接命中,含通配符命中)
        const normalNodes: FastEventListenerNode[] = [];
        if (singleSegment) {
            // 顺序与 _traverseToPath 一致：先通配后精确
            if ("*" in root) normalNodes.push(root["*"]);
            if ("**" in root) normalNodes.push(root["**"]);
            if (type in root) normalNodes.push(root[type]);
        } else {
            this._traverseToPath(root, parts!, (node) => {
                normalNodes.push(node);
            });
        }

        // hooks 只对原始 emit 触发一次(broadcast 后代不单独触发 hook)
        const r = this._executeHooks("BeforeExecuteListener", [message, args]);
        if (Array.isArray(r)) {
            return r;
        } else if (r === false) {
            throw new AbortError(message.type);
        }

        // 2. broadcast 后代目标收集 + 改写(在 transform 前,回调收到原始 message/args)。
        //    后代基于原始 message 改写 type,后续各自 transform(先 broadcast 后 transform)。
        const broadcastItems: { node: FastEventListenerNode; descMsg: any; descArgs: any }[] = [];
        if (args.broadcast) {
            const targets = collectBroadcastTargets(
                root,
                singleSegment ? [type] : parts!,
                delimiter,
            );
            for (const { node, type: descType } of targets) {
                if (!node.__listeners || node.__listeners.length === 0) continue;
                let descMsg: any;
                let descArgs: any;
                if (args.broadcast === true) {
                    descMsg = { ...message, type: descType };
                    descArgs = { ...args };
                } else {
                    const ret = (args.broadcast as Function).call(this, descType, message, args);
                    if (!ret) continue;
                    if (Array.isArray(ret)) {
                        [descMsg, descArgs] = ret;
                    } else {
                        descMsg = ret;
                        descArgs = { ...args };
                    }
                }
                broadcastItems.push({ node, descMsg, descArgs });
            }
        }

        const results: any[] = [];

        // 3. 正常匹配:transform 保持突变(向后兼容,AfterExecuteListener 接收突变后 message)
        if (transformFn) {
            const transformed = transformFn.call(this, message as any);
            if (transformed !== message) {
                message.payload = transformed;
                args.rawEventType = message.type;
                args.flags = (args.flags || 0) | FastEventListenerFlags.Transformed;
            }
        }
        results.push(...this._executeListeners(normalNodes, message, args));

        // 4. 后代覆盖:各自 transform(基于原始 message 副本,不受正常突变影响),经 executor 执行
        for (const { node, descMsg, descArgs } of broadcastItems) {
            if (transformFn) {
                const transformed = transformFn.call(this, descMsg);
                if (transformed !== descMsg) {
                    descMsg.payload = transformed;
                    descArgs.rawEventType = descMsg.type;
                    descArgs.flags = (descArgs.flags || 0) | FastEventListenerFlags.Transformed;
                }
            }
            results.push(...this._executeListeners([node], descMsg, descArgs));
        }

        // 将 results 内部所有 expandable 的项展开，见 utils\expandable.ts 说明
        if (this._expandResults) {
            expandEmitResults(results);
        }
        this._executeHooks("AfterExecuteListener", [message, results, normalNodes] as any);
        return results;
    }

    /**
     * 发布端前缀广播快捷方法（等价于 emit(type, payload, { broadcast, retain })）。
     *
     * callback 省略时默认走 broadcast:true（后代监听器的 message.type 改写为
     * 后代完整路径）；传入 callback 则逐个后代改写，返回 null/falsy 跳过该后代。
     */
    public broadcast<R = any, T extends string = string>(
        type: ReplaceWildcard<T> | Types,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<AllEvents>, T>
            : any,
        callback?: (
            type: string,
            message: FastEventMessage,
            args: FastEventListenerArgs<any>,
        ) => [FastEventMessage, FastEventListenerArgs<any>] | FastEventMessage | null,
        retain?: boolean,
    ): R[] {
        const options = (
            retain ? { broadcast: callback ?? true, retain: true } : { broadcast: callback ?? true }
        ) as FastEventListenerArgs<any>;
        return this.emit(type as any, payload as any, options as any) as R[];
    }

    /**
     * 异步触发事件
     * @param type - 事件类型，可以是字符串或预定义的事件类型
     * @param payload - 事件数据负载
     * @param retain - 是否保留该事件，用于后续新的订阅者
     * @param meta - 事件元数据
     * @returns Promise，解析为所有监听器的执行结果数组
     *
     * @description
     * 异步触发指定类型的事件，与emit方法类似，但有以下区别：
     * - 返回Promise，等待所有异步监听器执行完成
     * - 使用Promise.allSettled处理监听器的执行结果
     * - 即使某些监听器失败，也会等待所有监听器执行完成
     * - 返回结果包含成功值或错误信息
     *
     * @example
     * ```ts
     * // 异步事件处理
     * const results = await emitter.emitAsync('data/process', rawData);
     *
     * // 处理结果包含成功和失败的情况
     * results.forEach(result => {
     *   if (result instanceof Error) {
     *     console.error('处理失败:', result);
     *   } else {
     *     console.log('处理成功:', result);
     *   }
     * });
     *
     * // 带元数据的异步事件
     * await emitter.emitAsync('batch/process', items, false, {
     *   batchId: 'batch-001',
     *   timestamp: Date.now()
     * });
     * ```
     */
    public async emitAsync<R = any, T extends Types = Types>(
        type: T,
        payload?: UnTransformedEvents<AllEvents>[T],
        retain?: boolean,
    ): Promise<(R | Error)[]>;
    public async emitAsync<R = any, T extends string = string>(
        type: ReplaceWildcard<T> | Types,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<AllEvents>, T>
            : any,
        retain?: boolean,
    ): Promise<(R | Error)[]>;
    public async emitAsync<R = any, T extends string = string>(
        type: ReplaceWildcard<T> | Types,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<AllEvents>, T>
            : any,
        options?: FastEventListenerArgs<Partial<Meta>>,
    ): Promise<(R | Error)[]>;
    public async emitAsync<R = any, T extends KeyOf<AllEvents> = KeyOf<AllEvents>>(
        message: FastEventEmitMessage<T, UnTransformedEvents<AllEvents>[T], Partial<Meta>>,
        retain?: boolean,
    ): Promise<(R | Error)[]>;
    public async emitAsync<R = any>(
        message: MutableMessage<AllEvents, Meta>,
        retain?: boolean,
    ): Promise<(R | Error)[]>;
    public async emitAsync<R = any>(
        message: {
            type: keyof AllEvents;
        },
        retain?: boolean,
    ): Promise<(R | Error)[]>;
    public async emitAsync<R = any, T extends string = string>(
        type: T,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<AllEvents>, T>
            : any,
        retain?: boolean,
    ): Promise<(R | Error)[]>;

    public async emitAsync<R = any>(): Promise<(R | Error)[]> {
        const results = await Promise.allSettled(this.emit.apply(this, arguments as any));
        return getPromiseResults(results);
    }

    /**
     * 等待指定事件发生，返回一个Promise
     * @param type - 要等待的事件类型
     * @param timeout - 超时时间（毫秒），默认为0表示永不超时
     * @returns Promise，解析为事件消息对象，包含type、payload和meta
     *
     * @description
     * 创建一个Promise，在指定事件发生时解析。
     * - 当事件触发时，Promise会解析为事件消息对象
     * - 如果设置了timeout且超时，Promise会被拒绝
     * - 一旦事件发生或超时，会自动取消事件监听
     *
     * @example
     * ```ts
     * try {
     *   // 等待登录事件，最多等待5秒
     *   const event = await emitter.waitFor('user/login', 5000);
     *   console.log('用户登录成功:', event.payload);
     * } catch (error) {
     *   console.error('等待登录超时');
     * }
     *
     * // 无限等待事件
     * const event = await emitter.waitFor('server/ready');
     * console.log('服务器就绪');
     *
     * // 等待指定的事件发生，并且如果该事件有其他订阅者，则同时等待该事件的所有订阅执行完成
     * waitFor
     *
     *
     *
     * ```
     */
    public waitFor<T extends string = KeyOf<AllEvents>>(
        type: T,
        timeout?: number,
    ): Promise<
        T extends IsTransformedEvent<AllEvents, T>
            ? PickPayload<ValueOf<GetClosestEvents<Events, T>>>
            : FastEventMessage<T, GetClosestEventPayload<AllEvents, T>, Meta>
    >;
    public waitFor<T extends string = string>(
        type: T,
        timeout?: number,
    ): Promise<TypedFastEventMessage<AllEvents, Meta>>;

    public waitFor(): Promise<any> {
        const type = arguments[0] as any;
        const timeout = arguments[1] as number;
        return new Promise<any>((resolve, reject) => {
            let tid: any;
            let subscriber: FastEventSubscriber;
            const listener = (message: any) => {
                clearTimeout(tid);
                subscriber?.off();
                resolve(message);
            };
            if (timeout && timeout > 0) {
                tid = setTimeout(() => {
                    subscriber?.off();
                    reject(new Error("wait for event<" + type + "> is timeout"));
                }, timeout);
            }
            // 订阅事件
            subscriber = this.on(type, listener as any) as unknown as FastEventSubscriber;
        });
    }

    /**
     * 创建一个新的事件作用域
     * @param prefix - 作用域前缀，将自动添加到该作用域下所有事件名称前
     * @returns 新的FastEventScope实例
     *
     * @description
     * 创建一个新的事件作用域，用于在特定命名空间下管理事件。
     *
     * 重要特性：
     * - 作用域与父事件发射器共享同一个监听器表
     * - 作用域中的事件会自动添加前缀
     * - 作用域的所有操作都会映射到父事件发射器上
     * - 作用域不是完全隔离的，只是提供了事件名称的命名空间
     *
     * @example
     * ```ts
     * const emitter = new FastEvent();
     *
     * // 创建用户相关事件的作用域
     * const userEvents = emitter.scope('user');
     *
     * // 在作用域中监听事件
     * userEvents.on('login', (data) => {
     *   // 实际监听的是 'user/login'
     *   console.log('用户登录:', data);
     * });
     *
     * // 在作用域中触发事件
     * userEvents.emit('login', { userId: 123 });
     * // 等同于 emitter.emit('user/login', { userId: 123 })
     *
     * // 创建嵌套作用域
     * const profileEvents = userEvents.scope('profile');
     * profileEvents.emit('update', { name: 'John' });
     * // 等同于 emitter.emit('user/profile/update', { name: 'John' })
     *
     * // 清理作用域
     * userEvents.offAll();  // 清理 'user' 前缀下的所有事件
     * ```
     */
    scope<P extends string = string, ScopeInstance extends IFastEventScope = IFastEventScope>(
        prefix: P,
        scopeObj: ScopeInstance,
        options?: DeepPartial<FastEventScopeOptions<Meta>>,
    ): FastEventScope<
        RemoveAnyRecord<ScopeEvents<AllEvents, P> & ScopeInstance["__events__"]>,
        RemoveAnyRecord<Meta & ScopeInstance["__meta__"]>,
        RemoveAnyRecord<Context & ScopeInstance["__context__"]>
    > &
        ScopeInstance;
    scope<
        E = unknown, //用于扩展事件
        P extends string = string,
        M extends Record<string, any> = Record<string, any>,
        C = Context,
    >(
        prefix: P,
        options?: DeepPartial<FastEventScopeOptions<Meta & M, C>>,
    ): FastEventScope<ScopeEvents<Events, P> & E, Meta & M, C>;
    scope() {
        const [prefix, scopeObj, options] = parseScopeArgs(
            arguments,
            this.options.meta,
            this.options.context,
        );
        let scope;
        if (scopeObj) {
            scope = scopeObj;
        } else {
            scope = new FastEventScope();
        }
        scope.bind(this as any, prefix, options);
        return scope;
    }
}
